/**
 * ModoMazmorra — Una partida con principio, oleadas, jefe y final.
 *
 * Es lo que FedeWorld no tenía y Dungeons sí: un motivo para entrar, veinte
 * minutos de tensión creciente y salir con algo. El mundo abierto está muy
 * bien para construir, pero no cuenta ninguna historia por sí solo.
 *
 * No inventa contenido: orquesta lo que ya existe —HostileMobs, BossSystem,
 * el botín de Rareza— y le pone una forma. Ese es el truco: FedeWorld ya tenía
 * las piezas de un ARPG sueltas por el código.
 *
 * La curva está pensada para un niño de nueve: cinco oleadas cortas, cada una
 * un poco más dura, y un jefe al final. Ni una mazmorra infinita ni un pasillo
 * de dos minutos.
 */

export const OLEADAS = [
  { n: 1, mobs: 3,  tipos: ['zombie'],                      aviso: 'Oleada 1 — llegan zombis' },
  { n: 2, mobs: 4,  tipos: ['zombie', 'skeleton'],          aviso: 'Oleada 2 — y esqueletos' },
  { n: 3, mobs: 5,  tipos: ['skeleton', 'spider'],          aviso: 'Oleada 3 — arañas' },
  { n: 4, mobs: 6,  tipos: ['zombie', 'spider', 'creeper'], aviso: 'Oleada 4 — cuidado con los creepers' },
  { n: 5, mobs: 7,  tipos: ['skeleton', 'creeper'],         aviso: 'Oleada 5 — la última antes del jefe' },
];

/** Cuánto botín suelta cada tramo, y con cuánta suerte extra. */
const PREMIO_OLEADA = { objetos: 2, suerte: 0 };
const PREMIO_JEFE   = { objetos: 5, suerte: 120 };

export const ESTADOS = ['fuera', 'oleada', 'descanso', 'jefe', 'ganada', 'perdida'];

export class ModoMazmorra {
  /**
   * @param {object} ganchos
   *   onAviso(texto)               — mensaje al jugador
   *   onGenerarMobs(tipos, n)      — invoca mobs; devuelve el array creado
   *   onInvocarJefe()              — saca el jefe
   *   onBotin(cuantos, suerte)     — suelta botín
   *   onFin(gano, estadisticas)    — termina
   *   vivos()                      — cuántos enemigos de la mazmorra quedan
   */
  constructor(ganchos = {}) {
    this.g = ganchos;
    this.estado = 'fuera';
    this.oleada = 0;
    this.tiempo = 0;
    this.descanso = 0;
    this.bajas = 0;
    this.muertesJugador = 0;
  }

  get activa() { return this.estado !== 'fuera' && this.estado !== 'ganada' && this.estado !== 'perdida'; }

  empezar() {
    this.estado = 'oleada';
    this.oleada = 0;
    this.tiempo = 0;
    this.descanso = 0;
    this.bajas = 0;
    this.muertesJugador = 0;
    this._lanzarOleada();
  }

  _lanzarOleada() {
    const o = OLEADAS[this.oleada];
    this.g.onAviso?.(o.aviso);
    this.g.onGenerarMobs?.(o.tipos, o.mobs);
  }

  /** Se llama cuando muere un enemigo de la mazmorra. */
  bajaEnemigo() {
    if (!this.activa) return;
    this.bajas++;
  }

  /** Se llama si muere el jugador: la mazmorra se pierde entera. */
  muerteJugador() {
    if (!this.activa) return;
    this.muertesJugador++;
    this.estado = 'perdida';
    this.g.onAviso?.('Te han vencido. La mazmorra se cierra.');
    this.g.onFin?.(false, this.resumen());
  }

  update(dt) {
    if (!this.activa) return;
    this.tiempo += dt;

    if (this.estado === 'descanso') {
      this.descanso -= dt;
      if (this.descanso <= 0) {
        this.estado = this.oleada >= OLEADAS.length ? 'jefe' : 'oleada';
        if (this.estado === 'jefe') {
          this.g.onAviso?.('¡El jefe!');
          this.g.onInvocarJefe?.();
        } else {
          this._lanzarOleada();
        }
      }
      return;
    }

    const quedan = this.g.vivos?.() ?? 0;
    if (quedan > 0) return;

    if (this.estado === 'oleada') {
      this.oleada++;
      this.g.onBotin?.(PREMIO_OLEADA.objetos, PREMIO_OLEADA.suerte);
      /* Un respiro entre oleadas: sin él, la pelea es un muro y no una curva.
         Es tiempo para recoger el botín y mirar qué ha caído. */
      this.estado = 'descanso';
      this.descanso = 6;
      this.g.onAviso?.(this.oleada >= OLEADAS.length
        ? 'Prepárate: viene el jefe'
        : `Oleada ${this.oleada} superada`);
      return;
    }

    if (this.estado === 'jefe') {
      this.estado = 'ganada';
      this.g.onBotin?.(PREMIO_JEFE.objetos, PREMIO_JEFE.suerte);
      this.g.onAviso?.('¡Mazmorra completada!');
      this.g.onFin?.(true, this.resumen());
    }
  }

  abandonar() {
    if (!this.activa) return;
    this.estado = 'fuera';
    this.g.onAviso?.('Has salido de la mazmorra.');
    this.g.onFin?.(false, this.resumen());
  }

  resumen() {
    return {
      oleadasSuperadas: this.oleada,
      totalOleadas: OLEADAS.length,
      bajas: this.bajas,
      segundos: Math.round(this.tiempo),
      gano: this.estado === 'ganada',
    };
  }
}
